import type { WorkspaceHero } from '../types/focus_area'
import type { Demand } from '../types/demand'
import { daysInStage } from './demandSla'

export const HERO_SLA_DAYS = 14

/*
 * A function called `daysSinceDemandActivity` used to live here, and its whole
 * body was `return daysInStage(demand)` (#1258).
 *
 * The BEHAVIOUR was right and the NAME was wrong, so the name went. These are
 * decision-queue signals: a demand that has sat in Triaging for forty days is
 * overdue for a decision whether or not people have been commenting on it, and
 * comments do not make the decision. Redefining them to follow real activity
 * would have quietly re-ordered council's triage queue — a product change
 * nobody asked for, made under cover of a rename.
 *
 * Real activity is `last_activity_at`, computed server-side over six sources
 * (backend-python/services/demand_activity.py). Two things named for activity
 * that measure different things would be worse than the one wrong name.
 */
export function isPostedWithoutApplications(
  demand: Pick<Demand, 'stage' | 'staffed_status' | 'application_count'>,
): boolean {
  // Posted = resourcing and not yet staffed (a staffed demand isn't an open
  // posting seeking applicants).
  return (
    demand.stage === 'resourcing' &&
    demand.staffed_status !== 'on' &&
    (demand.application_count ?? 0) === 0
  )
}

export function isStalePosting(
  demand: Pick<Demand, 'stage' | 'stage_entered_at' | 'submission_date' | 'created_at'>,
  staleDays = HERO_SLA_DAYS,
): boolean {
  return demand.stage === 'resourcing' && daysInStage(demand) > staleDays
}

export function isOverdueDecision(
  demand: Pick<Demand, 'stage' | 'stage_entered_at' | 'submission_date' | 'created_at'>,
  slaDays = HERO_SLA_DAYS,
): boolean {
  return (
    (demand.stage === 'scoping' || demand.stage === 'triaging') &&
    daysInStage(demand) > slaDays
  )
}

export function needsCouncilAction(demand: Demand, staleDays = HERO_SLA_DAYS): boolean {
  return (
    demand.stage === 'scoping' ||
    demand.stage === 'triaging' ||
    (isPostedWithoutApplications(demand) && isStalePosting(demand, staleDays))
  )
}

export function calculateWorkspaceHeroMetrics(
  demands: Demand[],
  staleDays = HERO_SLA_DAYS,
): WorkspaceHero {
  let overdue = 0
  let pending = 0
  let postedNoApps = 0
  let stale14d = 0

  for (const demand of demands) {
    if (isOverdueDecision(demand, staleDays)) overdue += 1
    if (needsCouncilAction(demand, staleDays)) pending += 1
    if (isPostedWithoutApplications(demand)) postedNoApps += 1
    if (isStalePosting(demand, staleDays)) stale14d += 1
  }

  return {
    overdue,
    pending,
    posted_no_apps: postedNoApps,
    stale_14d: stale14d,
  }
}
